import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

export interface PluginBanner {
  id: string;
  plugin_id: string;
  color: string;
  text: string;
  icon: string;
  enabled: boolean;
  created_at: string;
  // Enhanced customization properties
  banner_position: 'header' | 'footer' | 'left-vertical' | 'right-vertical';
  alignment: 'left' | 'center' | 'right';
  banner_type: 'overlay' | 'card';
  border_style: 'solid' | 'dashed' | 'dotted';
  border_width: number;
  border_radius: number;
  padding: number;
  font_size: number;
  font_weight: 'normal' | 'medium' | 'semibold' | 'bold';
  background_opacity: number;
  animation_enabled: boolean;
  animation_type: 'none' | 'pulse' | 'bounce' | 'fade' | 'slide' | 'glow' | 'shake' | 'rotate' | 'flip';
  animation_duration: number;
  animation_delay: number;
  full_width: boolean;
  margin: number;
  // NEW ENHANCED FEATURES
  priority: number;
  icon_enabled: boolean;
  icon_size: number;
  card_icon_position: 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-center' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  card_icon_placement: 'inside' | 'outside' | 'overlay';
  overlay_text_align: 'left' | 'center' | 'right';
  overlay_icon_position: 'left' | 'right' | 'both' | 'stationary-left' | 'stationary-right' | 'stationary-center';
  // Computed properties for display
  background_color: string;
  text_color: string;
  icon_name: string;
  icon_filter: string;
  border_color: string;
  is_warning?: boolean;
  warning_subtext?: string;
}

// Helper function to get CSS filter for icon color matching
const getColorFilter = (color: string): string => {
  const hex = color.replace('#', '');
  
  const colorFilters: { [key: string]: string } = {
    'ef4444': 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
    'dc2626': 'brightness(0) saturate(100%) invert(22%) sepia(77%) saturate(6236%) hue-rotate(354deg) brightness(91%) contrast(94%)',
    'f97316': 'brightness(0) saturate(100%) invert(58%) sepia(79%) saturate(2476%) hue-rotate(24deg) brightness(95%) contrast(105%)',
    'ea580c': 'brightness(0) saturate(100%) invert(48%) sepia(96%) saturate(4161%) hue-rotate(14deg) brightness(96%) contrast(96%)',
    'eab308': 'brightness(0) saturate(100%) invert(85%) sepia(50%) saturate(586%) hue-rotate(1deg) brightness(104%) contrast(107%)',
    'ca8a04': 'brightness(0) saturate(100%) invert(65%) sepia(84%) saturate(1178%) hue-rotate(6deg) brightness(91%) contrast(96%)',
    '22c55e': 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)',
    '16a34a': 'brightness(0) saturate(100%) invert(39%) sepia(84%) saturate(1783%) hue-rotate(86deg) brightness(96%) contrast(90%)',
    '3b82f6': 'brightness(0) saturate(100%) invert(40%) sepia(93%) saturate(1352%) hue-rotate(214deg) brightness(100%) contrast(97%)',
    '2563eb': 'brightness(0) saturate(100%) invert(29%) sepia(94%) saturate(7463%) hue-rotate(213deg) brightness(93%) contrast(94%)',
    '8b5cf6': 'brightness(0) saturate(100%) invert(55%) sepia(88%) saturate(3200%) hue-rotate(257deg) brightness(97%) contrast(93%)',
    '7c3aed': 'brightness(0) saturate(100%) invert(29%) sepia(89%) saturate(7048%) hue-rotate(268deg) brightness(90%) contrast(96%)',
    'ec4899': 'brightness(0) saturate(100%) invert(47%) sepia(74%) saturate(6187%) hue-rotate(301deg) brightness(90%) contrast(89%)',
    'd946ef': 'brightness(0) saturate(100%) invert(64%) sepia(18%) saturate(3258%) hue-rotate(287deg) brightness(105%) contrast(97%)',
    '6b7280': 'brightness(0) saturate(100%) invert(60%) sepia(8%) saturate(878%) hue-rotate(157deg) brightness(90%) contrast(87%)',
    '4b5563': 'brightness(0) saturate(100%) invert(37%) sepia(15%) saturate(709%) hue-rotate(169deg) brightness(89%) contrast(86%)',
  };
  
  return colorFilters[hex.toLowerCase()] || 'brightness(0) saturate(100%) invert(50%) sepia(100%) saturate(200%) hue-rotate(0deg)';
};

// Animation class mapping
const getAnimationClass = (type: string, duration: number): string => {
  const baseClasses = {
    'none': '',
    'pulse': 'animate-pulse',
    'bounce': 'animate-bounce',
    'fade': 'animate-pulse',
    'slide': 'animate-pulse',
    'glow': 'animate-ping',
    'shake': 'animate-bounce',
    'rotate': 'animate-spin',
    'flip': 'animate-pulse'
  };
  
  return baseClasses[type as keyof typeof baseClasses] || '';
};

export const usePluginBanners = () => {
  const [banners, setBanners] = useState<PluginBanner[]>([]);
  const [loading, setLoading] = useState(true);

  // Helper function to convert hex color to rgba with opacity
  const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  useEffect(() => {
    loadBanners();
    
    // Set up real-time subscription for banner changes
    const subscription = supabase
      .channel('plugin_banners')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'plugin_banners' },
        () => {
          loadBanners();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('plugin_banners')
        .select('*')
        .eq('enabled', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform data to include computed properties
      const transformedBanners = (data || []).map(banner => ({
        ...banner,
        // Ensure all new fields have defaults if not present
        banner_position: banner.banner_position || 'header',
        alignment: banner.alignment || 'left',
        banner_type: banner.banner_type || 'overlay',
        border_style: banner.border_style || 'dashed',
        border_width: banner.border_width || 2,
        border_radius: banner.border_radius || 8,
        padding: banner.padding || 12,
        font_size: banner.font_size || 14,
        font_weight: banner.font_weight || 'semibold',
        background_opacity: banner.background_opacity || 0.15,
        animation_enabled: banner.animation_enabled || false,
        animation_type: banner.animation_type || 'none',
        animation_duration: banner.animation_duration || 2000,
        animation_delay: banner.animation_delay || 0,
        full_width: banner.full_width || false,
        margin: banner.margin || 8,
        // NEW ENHANCED FEATURES DEFAULTS
        priority: banner.priority || 0,
        icon_enabled: banner.icon_enabled !== undefined ? banner.icon_enabled : true,
        icon_size: banner.icon_size || 16,
        card_icon_position: banner.card_icon_position || 'top-left',
        card_icon_placement: banner.card_icon_placement || 'overlay',
        overlay_text_align: banner.overlay_text_align || 'center',
        overlay_icon_position: banner.overlay_icon_position || 'left',
        // Computed display properties
        background_color: hexToRgba(banner.color, banner.background_opacity || 0.15),
        text_color: banner.color,
        icon_name: banner.icon,
        icon_filter: getColorFilter(banner.color),
        border_color: banner.color,
        is_warning: false
      }));
      
      setBanners(transformedBanners);
    } catch (error) {
      console.error('Error loading banners:', error);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to get automatic version-based warning banners
  const getVersionBanner = (buildType: string): PluginBanner | null => {
    const versionBanners = {
      'Beta': {
        color: '#eab308',
        text: 'BETA VERSION',
        icon: 'warning-box',
        subtext: 'This is a pre-release version that may contain bugs or incomplete features. Use with caution.',
        animation_type: 'pulse' as const
      },
      'Alpha': {
        color: '#f97316',
        text: 'ALPHA VERSION',
        icon: 'warning-box',
        subtext: 'This is an early test version with limited stability. Expect frequent changes and potential issues.',
        animation_type: 'glow' as const
      },
      'DEV': {
        color: '#ef4444',
        text: 'DEV VERSION',
        icon: 'warning-box',
        subtext: 'This is a development build for testing only. Not recommended for production use.',
        animation_type: 'shake' as const
      }
    };

    const bannerConfig = versionBanners[buildType as keyof typeof versionBanners];
    if (!bannerConfig) return null;

    return {
      id: `version-${buildType.toLowerCase()}`,
      plugin_id: 'version-banner',
      color: bannerConfig.color,
      text: bannerConfig.text,
      icon: bannerConfig.icon,
      enabled: true,
      created_at: new Date().toISOString(),
      // Fixed warning banner styling
      banner_position: 'header',
      alignment: 'center',
      banner_type: 'overlay',
      border_style: 'dashed',
      border_width: 2,
      border_radius: 8,
      padding: 16,
      font_size: 16,
      font_weight: 'bold',
      background_opacity: 0.15,
      animation_enabled: true,
      animation_type: bannerConfig.animation_type,
      animation_duration: 3000,
      animation_delay: 500,
      full_width: true,
      margin: 0,
      // NEW ENHANCED FEATURES
      priority: buildType === 'DEV' ? 15 : buildType === 'Alpha' ? 10 : 5,
      icon_enabled: true,
      icon_size: 20,
      card_icon_position: 'top-right',
      card_icon_placement: 'overlay',
      overlay_text_align: 'center',
      overlay_icon_position: 'left',
      // Computed properties
      background_color: hexToRgba(bannerConfig.color, 0.15),
      text_color: bannerConfig.color,
      icon_name: bannerConfig.icon,
      icon_filter: getColorFilter(bannerConfig.color),
      border_color: bannerConfig.color,
      is_warning: true,
      warning_subtext: bannerConfig.subtext
    };
  };

  const getBannersForPlugin = (pluginId: string, selectedBuildType?: string) => {
    const customBanners = banners.filter(banner => banner.plugin_id === pluginId && banner.enabled);
    const warnings: PluginBanner[] = [];
    
    // Add automatic version banner for unstable versions
    if (selectedBuildType && ['Beta', 'Alpha', 'DEV'].includes(selectedBuildType)) {
      const versionBanner = getVersionBanner(selectedBuildType);
      if (versionBanner) {
        warnings.push(versionBanner);
      }
    }
    
    // Group banners by position
    const bannersByPosition = {
      header: [...warnings, ...customBanners.filter(b => b.banner_position === 'header')],
      footer: customBanners.filter(b => b.banner_position === 'footer'),
      'left-vertical': customBanners.filter(b => b.banner_position === 'left-vertical'),
      'right-vertical': customBanners.filter(b => b.banner_position === 'right-vertical')
    };
    
    return bannersByPosition;
  };

  const getBannerStyles = (banner: PluginBanner) => {
    const animationClass = banner.animation_enabled ? getAnimationClass(banner.animation_type, banner.animation_duration) : '';
    
    const baseStyles = {
      backgroundColor: banner.background_color,
      borderColor: banner.border_color,
      color: banner.text_color,
      borderStyle: banner.border_style,
      borderWidth: `${banner.border_width}px`,
      borderRadius: `${banner.border_radius}px`,
      padding: `${banner.padding}px`,
      fontSize: `${banner.font_size}px`,
      fontWeight: banner.font_weight,
      margin: `${banner.margin}px`,
      width: banner.full_width ? '100%' : 'auto',
      animationDuration: banner.animation_enabled ? `${banner.animation_duration}ms` : undefined,
      animationDelay: banner.animation_enabled ? `${banner.animation_delay}ms` : undefined
    };

    return { styles: baseStyles, className: animationClass };
  };

  return {
    banners,
    getBannersForPlugin,
    getVersionBanner,
    getBannerStyles,
    loading,
    loadBanners
  };
}; 